import {
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/system/Box';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// COMPONENTS
import LanguagePicker from '../../../common/LanguagePicker';
import CarrierSingleView from './Carrier/CarrierSingleView';
import PrefixMapSingleView from './PrefixMapSingleView';

// UTILS
import { deepCompare } from '../../../../utils/compare';

// NETWORK
import useAdminCountryRequest from '../../../../requests/AdminCountryRequest';
import CountryCarriers from './CountryCarriers';
import CountryPrefixMaps from './CountryPrefixMaps';
import CountryViewLander from './CountryViewLander';

// BREADCRUMB
import { useBreadcrumb } from './BreadcrumbContext';

const ServiceCountrySingleView = ({ country, errorHandler, navigate }) => {
  const params = useParams();
  const { child, childId } = params;

  const { addBreadcrumb, dropBreadcrumb } = useBreadcrumb();

  const [loading, setLoading] = useState(true);

  const [view, setView] = useState({});
  const [carriers, setCarriers] = useState([]);
  const [prefixMaps, setPrefixMaps] = useState([]);

  const [selectedCarrier, setSelectedCarrier] = useState({});
  const [selectedPrefixMap, setSelectedPrefixMap] = useState({});

  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});

  const [addNewChild, setAddNewChild] = useState(false);
  const { getCarriersByCountry, getPrefixMapsByCountry, updateCountryById } =
    useAdminCountryRequest();

  useEffect(() => {
    addBreadcrumb(0, `Country: ${country?.country_name || 'NEW'}`);
    return () => {
      dropBreadcrumb(0);
    };
  }, [country]);

  useEffect(() => {
    if (!deepCompare(country, view)) {
      setView(country);
      fetchCarriersAndPrefixMaps();
    }
  }, [country]);

  useEffect(() => {
    if (child === 'carrier') {
      if (childId === '0') {
        setSelectedCarrier({});
        setAddNewChild(true);
        return;
      }

      if (carriers.length > 0) {
        setSelectedCarrier(
          carriers.find((carrier) => carrier.carrier_id === parseInt(childId))
        );
      }
    }

    if (child === 'prefix-map') {
      if (childId === '0') {
        setSelectedPrefixMap({});
        setAddNewChild(true);
        return;
      }

      if (prefixMaps.length > 0) {
        setSelectedPrefixMap(
          prefixMaps.find(
            (prefixMap) => prefixMap.country_prefix_map_id === parseInt(childId)
          )
        );
      }
    }
  }, [carriers, prefixMaps, child, childId]);

  useEffect(() => {
    if (!params.child) {
      setSelectedCarrier({});
      setSelectedPrefixMap({});
      setAddNewChild(false);
    }
    fetchCarriersAndPrefixMaps();
  }, [params]);

  let fetching = false;
  const fetchCarriersAndPrefixMaps = async () => {
    if (!country || fetching) return;

    fetching = true;
    setLoading(true);

    try {
      const [fetchedCarriers, fetchedPrefixMaps] = await Promise.all([
        getCarriersByCountry(country.country_code_id, errorHandler),
        getPrefixMapsByCountry(country.country_code_id, errorHandler),
      ]);

      setCarriers(fetchedCarriers);
      setPrefixMaps(fetchedPrefixMaps);
    } catch (error) {
      console.error('Error fetching carriers and prefix maps:', error);
      errorHandler(error);
    }

    setLoading(false);
    fetching = false;
  };

  const toggleEdit = () => {
    setIsEditing((prevIsEditing) => !prevIsEditing);
  };

  /*
    Handles changes in the form
  */
  const handleFormChange = (name, value) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  /*
    Handles Edit form submission
  */
  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      setView(
        await updateCountryById(view.country_code_id, edits, errorHandler)
      );

      toggleEdit();
    } catch (e) {
      console.log('Got Country update err:', e);
    }
  };

  /*
    Convenience function to generate Form Groups
  */
  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  if (child === 'prefix-map') {
    return (
      <PrefixMapSingleView
        country={view}
        prefixMap={selectedPrefixMap}
        editing={addNewChild}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    );
  }

  if (child === 'carrier') {
    return (
      <CarrierSingleView
        carrier={selectedCarrier}
        country={view}
        editing={addNewChild}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    );
  }

  if (!view) {
    return <Typography>(Country) No Data</Typography>;
  }

  if (isEditing) {
    return (
      <Box>
        <CountryViewLander country={view} navigate={navigate} />

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ maxWidth: 800, marginBottom: 4 }}
        >
          <Grid container spacing={2} mt={2}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 280 }} required>
                  Country Name
                </InputLabel>
                <TextField
                  name="country_name"
                  required
                  variant="outlined"
                  fullWidth
                  size="small"
                  defaultValue={view.country_name}
                  onInput={(e) =>
                    handleFormChange(e.target.name, e.target.value)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 280 }} required>
                  Country Code
                </InputLabel>
                <TextField
                  name="country_code"
                  required
                  variant="outlined"
                  fullWidth
                  size="small"
                  defaultValue={view.country_code}
                  onInput={(e) =>
                    handleFormChange(e.target.name, e.target.value)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 280 }} required>
                  Customers Located Here:
                </InputLabel>
                <Select
                  name="country_status"
                  defaultValue={view.country_status}
                  onChange={(e) =>
                    handleFormChange(e.target.name, e.target.value)
                  }
                  required
                  fullWidth
                  size="small"
                >
                  <MenuItem value="Y">Yes</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 280 }} required>
                  Premium Charged For Messages:
                </InputLabel>
                <Select
                  name="premium_status"
                  defaultValue={view.premium_status}
                  onChange={(e) =>
                    handleFormChange(e.target.name, e.target.value)
                  }
                  required
                  fullWidth
                  size="small"
                >
                  <MenuItem value="Y">Yes</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 280 }} required>
                  Primary Language For This Country:
                </InputLabel>
                <LanguagePicker
                  selectName={'dflt_language_id'}
                  onChange={(e) => handleFormChange('dflt_language_id', e)}
                  selectedLanguageId={
                    edits.dflt_language_id || view.dflt_language_id
                  }
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={2}>
                <Button
                  onClick={toggleEdit}
                  color="secondary"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button variant="contained" type="submit" color="secondary">
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <CountryCarriers
          country={view}
          carriers={carriers}
          loading={loading}
          errorHandler={errorHandler}
          navigate={navigate}
        />

        <CountryPrefixMaps
          country={view}
          prefixMaps={prefixMaps}
          loading={loading}
          errorHandler={errorHandler}
          navigate={navigate}
        />
      </Box>
    );
  }

  return (
    <Stack gap={2}>
      <CountryViewLander country={view} navigate={navigate} />

      <Grid container spacing={2}>
        {generateFormGroup('Country Name:', view.country_name)}
        {generateFormGroup('Country Code:', view.country_code)}
        {generateFormGroup(
          'Customers Located Here:',
          view.country_status === 'Y' ? 'Yes' : 'No'
        )}
        {generateFormGroup(
          'Premium Charged For Messages:',
          view.premium_status === 'Y' ? 'Yes' : 'No'
        )}
        {generateFormGroup(
          'Primary Language For This Country:',
          view.language_name || 'English'
        )}
        <Button variant="contained" color="secondary" onClick={toggleEdit}>
          Edit
        </Button>
      </Grid>

      <CountryCarriers
        country={view}
        carriers={carriers}
        loading={loading}
        errorHandler={errorHandler}
        navigate={navigate}
      />

      <CountryPrefixMaps
        country={view}
        prefixMaps={prefixMaps}
        loading={loading}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    </Stack>
  );
};

export default ServiceCountrySingleView;
